import React, { ReactNode, useEffect, useMemo, useState } from "react";

type StepOnClickResult = boolean | void;

export interface Step {
    label?: ReactNode;
    content: ReactNode;
    validator: () => boolean;
    path?: string;
    condition?: () => boolean;
    onClick?: () => Promise<StepOnClickResult>;
    ctaText?: string;
    hideBack?: boolean;
}

interface HorizontalStepperProps {
    steps: Step[];
    initialStepIndex?: number;
    initialCompleted?: boolean[];
    onFinish?: (success: boolean) => void | Promise<void>;
}

const HorizontalStepper: React.FC<HorizontalStepperProps> = ({
    steps,
    initialStepIndex = 0,
    initialCompleted,
    onFinish,
}) => {
    const filteredSteps = useMemo(
        () => steps.filter((s) => (s.condition ? s.condition() : true)),
        [steps]
    );

    const [currentStepIndex, setCurrentStepIndex] = useState(initialStepIndex);
    const [completedSteps, setCompletedSteps] = useState<boolean[]>(
        initialCompleted ?? new Array(filteredSteps.length).fill(false)
    );
    const [loading, setLoading] = useState(false);

    // keep index valid
    useEffect(() => {
        setCurrentStepIndex((idx) =>
            initialStepIndex < filteredSteps.length
                ? initialStepIndex
                : Math.min(idx, filteredSteps.length - 1)
        );
    }, [initialStepIndex, filteredSteps.length]);

    // sync completed
    useEffect(() => {
        if (initialCompleted && initialCompleted.length === filteredSteps.length) {
            setCompletedSteps(initialCompleted);
        } else {
            setCompletedSteps((prev) => {
                const next = new Array(filteredSteps.length).fill(false);
                for (let i = 0; i < Math.min(prev.length, next.length); i++) next[i] = prev[i];
                return next;
            });
        }
    }, [initialCompleted, filteredSteps.length]);

    const currentStep = filteredSteps[currentStepIndex];
    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === filteredSteps.length - 1;

    const submitLabel = currentStep?.ctaText ?? (isLastStep ? "Finish" : "Submit");
    const shouldShowBack = !isFirstStep && !currentStep?.hideBack;

    const nextStep = async () => {
        if (!currentStep?.validator()) return;

        setLoading(true);
        try {
            let proceed = true;

            if (currentStep.onClick) {
                const out = await currentStep.onClick();
                proceed = out !== false;
            }

            if (!proceed) return;

            setCompletedSteps((prev) => {
                const updated = [...prev];
                updated[currentStepIndex] = true;
                return updated;
            });

            if (isLastStep) {
                await onFinish?.(true);
            } else {
                setCurrentStepIndex((i) => Math.min(i + 1, filteredSteps.length - 1));
            }
        } finally {
            setLoading(false);
        }
    };

    const previousStep = () => {
        if (currentStepIndex > 0) setCurrentStepIndex((i) => i - 1);
    };

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, [currentStepIndex]);

    return (
        <div className="flex flex-col space-y-[20px] pb-20 min-h-[calc(100vh-128px)]">

            {/* HORIZONTAL PROGRESS */}
            <div className="flex items-center w-full">
                {filteredSteps.map((_, index) => {
                    const isCurrent = index === currentStepIndex;
                    const isPast = index < currentStepIndex;
                    const isFutureCompleted = index > currentStepIndex && completedSteps[index];
                    const isFutureIncomplete = index > currentStepIndex && !completedSteps[index];

                    return (
                        <React.Fragment key={index}>
                            <div className="flex flex-col items-center">
                                <div
                                    className={`rounded-full w-[28px] h-[28px] flex items-center justify-center border text-[13px] font-semibold transition-all
${isCurrent
                                            ? "bg-white border-border-button-primary text-text-base"
                                            : isPast
                                                ? "bg-bg-button-primary border-bg-button-primary text-text-base"
                                                : isFutureCompleted
                                                    ? "bg-[#9fd3f7] border-[#9fd3f7] text-text-base"
                                                    : "bg-white border-[#e5e5e5] text-text-disabled"
                                        }
`}
                                >
                                    {index + 1}
                                </div>
                            </div>

                            {index < filteredSteps.length - 1 && (
                                <div
                                    className={`flex-1 h-[2px] transition-all
${completedSteps[index] || index < currentStepIndex
                                            ? "bg-bg-button-primary"
                                            : "bg-[#e5e5e5]"
                                        }
`}
                                />
                            )}

                        </React.Fragment>
                    );
                })}

            </div>

            {/* CONTENT */}
            <div
                key={currentStepIndex}
                className="transition-all duration-300 ease-out animate-step bg-page-body p-3 border border-border-subtle"
            >
                {currentStep?.label && (
                    <div className="text-[12px] font-medium rounded-lg  text-text-base mb-[8px] text-center md:text-left">
                        {currentStep.label}
                    </div>
                )}

                {currentStep?.content}
            </div>

            {/* ACTIONS */}
            <div className="flex justify-start gap-3 max-w-full">
                {shouldShowBack && (
                    <button
                        onClick={previousStep}
                        disabled={loading}
                        className="flex items-center justify-center gap-2 px-6 py-3 h-10 flex-1 text-text-button-tertiary border border-text-button-tertiary bg-transparent rounded-lg text-[12px]"
                    >
                        BACK
                    </button>
                )}

                <button
                    onClick={nextStep}
                    disabled={!currentStep?.validator() || loading}
                    className="flex items-center justify-center gap-2 px-6 py-3 h-10 flex-1 text-[12px] text-text-base font-medium rounded-lg bg-bg-button-primary disabled:bg-bg-disabled disabled:text-text-disabled"
                >
                    {submitLabel}
                </button>
            </div>
        </div>
    );
};

export default HorizontalStepper;
